/**
 * Email Subscribers Hook
 * Manages email subscriber data with caching, filtering, and optimistic updates
 *
 * Pattern: Adapted from useSmsSubscribers with email-specific enhancements
 * Date: 2025-10-26
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { EmailCustomer } from './types';
import { toast } from '@/components/ui/use-toast';

interface CacheEntry {
  data: EmailCustomer[];
  timestamp: number;
  fetchParams: string;
}

// Global cache shared across all hook instances
const globalCache = new Map<string, CacheEntry>();
const CACHE_TIME = 300000; // 5 minutes
let currentFetchPromise: Promise<any> | null = null;
let lastFetchTimestamp = 0;
const FETCH_DEBOUNCE = 500; // 500ms debounce

/**
 * Custom hook for managing email subscribers
 * @param user - Current user object with organisationId and restaurants
 * @param selectedRestaurantIds - Optional array of restaurant IDs to filter by
 */
export const useEmailSubscribers = (user?: any, selectedRestaurantIds?: string[]) => {
  const [subscribers, setSubscribers] = useState<EmailCustomer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Use refs to track current values without causing re-renders
  const selectedRestaurantIdsRef = useRef<string[] | undefined>(selectedRestaurantIds);
  const userIdRef = useRef<string | undefined>(user?.id);

  // Update refs when dependencies change
  useEffect(() => {
    const currentIds = selectedRestaurantIdsRef.current || [];
    const newIds = selectedRestaurantIds || [];

    if (
      user?.id !== userIdRef.current ||
      JSON.stringify([...currentIds].sort()) !== JSON.stringify([...newIds].sort())
    ) {
      selectedRestaurantIdsRef.current = selectedRestaurantIds;
      userIdRef.current = user?.id;
    }
  }, [user?.id, selectedRestaurantIds]);

  /**
   * Fetch email subscribers from the database
   * @param forceRefresh - Skip cache and fetch fresh data
   * @param targetSubscriberId - Specific subscriber ID to refresh (for targeted updates)
   */
  const fetchSubscribers = useCallback(
    async (forceRefresh = false, targetSubscriberId?: string) => {
      if (!user) {
        setSubscribers([]);
        setLoading(false);
        return;
      }

      const organisationId = user.organisationId || null;
      const restaurantIds =
        selectedRestaurantIdsRef.current || (user.restaurants?.map((r: any) => r.id) || []);

      const fetchParams = JSON.stringify({
        organisationId,
        restaurantIds: restaurantIds.length > 0 ? [...restaurantIds].sort() : 'all',
      });

      const cacheKey = `email-subscribers-${user.id}-${fetchParams}`;

      // Validate targetSubscriberId if provided
      const validTargetId =
        targetSubscriberId && typeof targetSubscriberId === 'string' ? targetSubscriberId : null;

      // Enhanced logging for targeted subscriber refresh
      if (validTargetId) {
        console.log(
          `[EMAIL-SUBSCRIBERS] Targeted refresh requested for subscriber: ${validTargetId}`
        );
      }

      // Log cache invalidation attempts
      if (forceRefresh) {
        console.log(
          `[EMAIL-SUBSCRIBERS] Force refreshing data, invalidating cache for key: ${cacheKey}`
        );

        if (globalCache.has(cacheKey)) {
          globalCache.delete(cacheKey);
          console.log(`[EMAIL-SUBSCRIBERS] Cache invalidated successfully for key: ${cacheKey}`);
        }
      } else if (globalCache.has(cacheKey)) {
        const cachedData = globalCache.get(cacheKey)!;
        const now = Date.now();

        // Check if we're targeting a specific subscriber for refresh
        if (!forceRefresh && validTargetId) {
          console.log(`[EMAIL-SUBSCRIBERS] Checking cache for targeted subscriber: ${validTargetId}`);

          // Use cached data but potentially update targeted subscriber
          setSubscribers(cachedData.data);
          setLoading(false);

          if (now - cachedData.timestamp < CACHE_TIME) {
            console.log(
              `[EMAIL-SUBSCRIBERS] Using cached data but will refresh targeted subscriber in background`
            );
          } else {
            return;
          }
        } else if (now - cachedData.timestamp < CACHE_TIME) {
          console.log(
            'Using cached email subscribers data, age:',
            Math.round((now - cachedData.timestamp) / 1000),
            's'
          );
          setSubscribers(cachedData.data);
          setLoading(false);
          return;
        } else {
          console.log(`[EMAIL-SUBSCRIBERS] Cache expired for key: ${cacheKey}, fetching fresh data`);
        }
      }

      // Cancel previous fetch if force refresh requested
      if (forceRefresh && currentFetchPromise) {
        console.log(
          '[EMAIL-SUBSCRIBERS] Force refresh requested while fetch in progress, cancelling previous fetch'
        );
        currentFetchPromise = null;
      } else if (currentFetchPromise) {
        console.log('[EMAIL-SUBSCRIBERS] Subscriber fetch already in progress, waiting for result');
        try {
          await currentFetchPromise;
          if (forceRefresh) {
            console.log('[EMAIL-SUBSCRIBERS] Force refresh still needed after waiting for in-progress fetch');
          } else {
            return;
          }
        } catch (err) {
          console.log('[EMAIL-SUBSCRIBERS] Previous fetch failed, retrying');
        }
      }

      const now = Date.now();
      // Skip debounce for force refresh
      if (!forceRefresh && now - lastFetchTimestamp < FETCH_DEBOUNCE) {
        console.log('[EMAIL-SUBSCRIBERS] Debouncing fetch request - too soon after previous request');
        return;
      }
      lastFetchTimestamp = now;

      setLoading(true);
      setError(null);

      try {
        console.log('Fetching email subscribers for:', {
          userId: user.id,
          organisationId,
          restaurantIds: restaurantIds.length > 0 ? restaurantIds : 'all',
        });

        if (!organisationId && restaurantIds.length === 0) {
          console.warn('No organization or restaurant access available');
          setSubscribers([]);
          setLoading(false);
          return;
        }

        // Use the optimized RPC function (materialized view)
        const fetchPromise = Promise.resolve(
          supabase.rpc('get_email_subscribers_fast', {
            p_organisation_id: organisationId,
            p_restaurant_ids: restaurantIds.length > 0 ? restaurantIds : null,
          })
        );

        currentFetchPromise = fetchPromise;

        const { data, error: queryError } = await fetchPromise;

        currentFetchPromise = null;

        if (queryError) {
          console.error('Error fetching email subscribers:', queryError);
          throw new Error(queryError.message || 'Failed to fetch subscribers');
        }

        console.log('Using email subscriber data from materialized view for optimal performance');

        // Group subscribers by ID (in case they appear in multiple restaurants)
        const subscribersMap = new Map();

        data.forEach((item: any) => {
          const subscriberId = item.id;

          if (!subscribersMap.has(subscriberId)) {
            subscribersMap.set(subscriberId, {
              id: item.id,
              name: item.name || 'Unknown',
              phone: item.phone || '',
              email: item.email || 'No email',
              status: mapSubscriptionStatus(item.subscription_status),
              lastOrder: item.latest_order_date ? item.latest_order_date : 'Never',
              lastOrderFormatted: item.latest_order_date
                ? new Date(item.latest_order_date).toLocaleDateString()
                : 'Never',
              messageCount: item.total_messages || 0,
              totalOrders: item.total_orders || 0,
              totalSpend: item.total_spend ? Number(item.total_spend).toFixed(2) : '0.00',
              restaurant_ids: [item.restaurant_id],
              opt_in_date: item.email_opt_in_date || undefined,
              opt_in_date_formatted: item.email_opt_in_date
                ? new Date(item.email_opt_in_date).toLocaleDateString()
                : undefined,
              opt_out_date: item.opt_out_timestamp || undefined,
              opt_out_date_formatted: item.opt_out_timestamp
                ? new Date(item.opt_out_timestamp).toLocaleDateString()
                : undefined,
              opt_out_source: item.opt_out_source || undefined,
              last_message_sent: item.last_message_date || undefined,
              last_message_sent_formatted: item.last_message_date
                ? new Date(item.last_message_date).toLocaleDateString()
                : undefined,
              customer_created_at: item.customer_created_at || undefined,
              loyaltyTier: determineLoyaltyTier(item.total_orders, item.total_spend),
              opt_in_source: formatOptInSource(item.opt_in_source),
              opt_out_source_formatted: formatOptOutSource(item.opt_out_source),
              // Email-specific fields
              has_hard_bounce: item.has_hard_bounce || false,
              last_bounce_date: item.last_bounce_date || undefined,
              has_complaint: item.has_complaint || false,
              last_complaint_date: item.last_complaint_date || undefined,
            });
          } else {
            // If subscriber appears in multiple restaurants, aggregate data
            const existingSubscriber = subscribersMap.get(subscriberId);

            // Update with most recent data if available
            if (
              item.latest_order_date &&
              (!existingSubscriber.lastOrder ||
                existingSubscriber.lastOrder === 'Never' ||
                new Date(item.latest_order_date) > new Date(existingSubscriber.lastOrder))
            ) {
              existingSubscriber.lastOrder = item.latest_order_date;
              existingSubscriber.lastOrderFormatted = new Date(
                item.latest_order_date
              ).toLocaleDateString();
            }

            // Add to total orders and spend
            existingSubscriber.totalOrders += item.total_orders || 0;

            const currentSpend = parseFloat(existingSubscriber.totalSpend) || 0;
            const additionalSpend = item.total_spend ? parseFloat(item.total_spend) : 0;
            existingSubscriber.totalSpend = (currentSpend + additionalSpend).toFixed(2);

            // Add restaurant ID if not already in array
            if (!existingSubscriber.restaurant_ids.includes(item.restaurant_id)) {
              existingSubscriber.restaurant_ids.push(item.restaurant_id);
            }

            // Update bounce/complaint status (if ANY restaurant has bounce/complaint, flag it)
            if (item.has_hard_bounce) {
              existingSubscriber.has_hard_bounce = true;
              if (
                !existingSubscriber.last_bounce_date ||
                (item.last_bounce_date &&
                  new Date(item.last_bounce_date) > new Date(existingSubscriber.last_bounce_date))
              ) {
                existingSubscriber.last_bounce_date = item.last_bounce_date;
              }
            }

            if (item.has_complaint) {
              existingSubscriber.has_complaint = true;
              if (
                !existingSubscriber.last_complaint_date ||
                (item.last_complaint_date &&
                  new Date(item.last_complaint_date) >
                    new Date(existingSubscriber.last_complaint_date))
              ) {
                existingSubscriber.last_complaint_date = item.last_complaint_date;
              }
            }
          }
        });

        const transformedData = Array.from(subscribersMap.values());

        // If we have a targeted subscriber ID, log if we found it
        if (targetSubscriberId && typeof targetSubscriberId === 'string') {
          const targetedSubscriber = transformedData.find((sub) => sub.id === targetSubscriberId);
          console.log(
            `[EMAIL-SUBSCRIBERS] Targeted refresh for ${targetSubscriberId}: ${
              targetedSubscriber ? 'Found in data' : 'Not found in data'
            }`
          );
          if (targetedSubscriber) {
            console.log(
              `[EMAIL-SUBSCRIBERS] Current status for ${targetSubscriberId}: ${targetedSubscriber.status}`
            );
          }
        }

        console.log(
          `Fetched ${data?.length || 0} email subscribers, consolidated to ${
            transformedData.length
          } unique customers`
        );

        // Update cache
        globalCache.set(cacheKey, {
          data: transformedData,
          timestamp: Date.now(),
          fetchParams,
        });

        setSubscribers(transformedData);
      } catch (err) {
        console.error('Error in useEmailSubscribers:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
        toast({
          title: 'Failed to load email subscribers',
          description: err instanceof Error ? err.message : 'Unknown error',
          variant: 'destructive',
        });

        setSubscribers([]);
        currentFetchPromise = null;
      } finally {
        setLoading(false);
      }
    },
    [user]
  );

  /**
   * Map subscription status from database to frontend format
   * Email has 4 states: active, opted_out, bounced, complained
   */
  const mapSubscriptionStatus = (subscriptionStatus: string): EmailCustomer['status'] => {
    switch (subscriptionStatus) {
      case 'active':
        return 'subscribed';
      case 'opted_out':
        return 'unsubscribed';
      case 'bounced':
        return 'bounced';
      case 'complained':
        return 'complained';
      case 'not_subscribed':
        return 'not_subscribed';
      default:
        console.warn('Unknown subscription status:', subscriptionStatus);
        return 'not_subscribed';
    }
  };

  /**
   * Determine loyalty tier based on order history
   */
  const determineLoyaltyTier = (totalOrders?: number, totalSpend?: number | string): string => {
    const orders = totalOrders || 0;
    const spend = typeof totalSpend === 'string' ? parseFloat(totalSpend || '0') : totalSpend || 0;

    if (orders >= 20 || spend >= 1000) return 'VIP';
    if (orders >= 10 || spend >= 500) return 'Gold';
    if (orders >= 5 || spend >= 250) return 'Silver';
    return 'Standard';
  };

  /**
   * Format opt-in source for display
   */
  const formatOptInSource = (source?: string | null): string => {
    switch (source) {
      case 'order_checkout':
        return 'Subscribed at Checkout';
      case 'admin_action':
        return 'Toggled by Admin';
      case 'migration':
        return 'Migrated Subscriber';
      case null:
      case undefined:
      case '':
        return 'Not subscribed';
      default:
        return source;
    }
  };

  /**
   * Format opt-out source for display
   */
  const formatOptOutSource = (source?: string | null): string => {
    switch (source) {
      case 'unsubscribe_link':
        return 'Clicked Unsubscribe Link';
      case 'admin_action':
        return 'Unsubscribed by Admin';
      case 'bounce':
        return 'Hard Bounce Detected';
      case 'complaint':
        return 'Spam Complaint Filed';
      case null:
      case undefined:
      case '':
        return 'Unknown';
      default:
        return source;
    }
  };

  // Initial fetch on mount
  useEffect(() => {
    if (user) {
      fetchSubscribers();
    }
  }, [user, fetchSubscribers]);

  return {
    subscribers,
    loading,
    error,
    // Standard force refresh - refreshes all subscribers
    refreshSubscribers: (force = true) => fetchSubscribers(force),
    // Targeted refresh with proper type checking
    refreshTargetedSubscriber: (subscriberId: string) => {
      // Validate subscriberId is a proper string
      if (!subscriberId || typeof subscriberId !== 'string') {
        console.warn(
          `[EMAIL-SUBSCRIBERS] Invalid subscriber ID format, expected string but got: ${typeof subscriberId}`
        );
        return;
      }

      // Force a refresh of data but focus on this specific subscriber
      fetchSubscribers(true, subscriberId);
    },
  };
};

export default useEmailSubscribers;
